import { Bot, Link2, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { AgentIntertwine } from '../agentIntertwine';

export function IntertwinePanel({
  intertwine,
  agentNameMap,
  onOpenAgent,
  compact = false,
}: {
  intertwine: AgentIntertwine;
  agentNameMap?: Map<string, string>;
  onOpenAgent?: (agentId: string) => void;
  compact?: boolean;
}) {
  const nameOf = (id: string) => agentNameMap?.get(id) || id;
  const hasAny =
    intertwine.downstreamAgents.length
    || intertwine.downstreamTools.length
    || intertwine.upstreamAgents.length;

  const Pill = ({
    id,
    icon,
    label,
  }: {
    id?: string;
    icon: 'agent' | 'tool';
    label: string;
  }) => {
    const clickable = icon === 'agent' && id && onOpenAgent;
    return (
      <Badge
        variant="outline"
        className={`text-xs font-normal ${clickable ? 'cursor-pointer hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200' : ''}`}
        onClick={clickable ? () => onOpenAgent?.(id) : undefined}
      >
        {icon === 'agent' ? <Bot className="w-3 h-3 mr-1" /> : <Wrench className="w-3 h-3 mr-1" />}
        {label}
      </Badge>
    );
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <Link2 className="w-4 h-4 text-violet-600" />
        <span className="font-medium">Intertwined</span>
        <span className="text-xs text-gray-500">
          {intertwine.connectionCount === 0
            ? 'No invoke_agent / MCP edges in this workflow'
            : `${intertwine.connectionCount} connection${intertwine.connectionCount === 1 ? '' : 's'} from volcano steps`}
        </span>
      </div>

      {!hasAny ? (
        <p className="text-xs text-gray-500">
          0 connections is honest when the workflow has no invoke_agent or MCP steps.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1.5">
              Downstream agents
            </p>
            <div className="flex flex-wrap gap-1.5">
              {intertwine.downstreamAgents.length === 0 ? (
                <span className="text-xs text-gray-400">None</span>
              ) : (
                intertwine.downstreamAgents.map((id) => (
                  <Pill key={id} id={id} icon="agent" label={nameOf(id)} />
                ))
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1.5">
              Downstream tools
            </p>
            <div className="flex flex-wrap gap-1.5">
              {intertwine.downstreamTools.length === 0 ? (
                <span className="text-xs text-gray-400">None</span>
              ) : (
                intertwine.downstreamTools.map((tool) => (
                  <Pill
                    key={tool.id}
                    icon="tool"
                    label={tool.mcp_server ? `${tool.mcp_server}/${tool.tool_name}` : tool.tool_name}
                  />
                ))
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1.5">
              Upstream agents
            </p>
            <div className="flex flex-wrap gap-1.5">
              {intertwine.upstreamAgents.length === 0 ? (
                <span className="text-xs text-gray-400">None recorded in this roster</span>
              ) : (
                intertwine.upstreamAgents.map((id) => (
                  <Pill key={id} id={id} icon="agent" label={nameOf(id)} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
